import Typography from '@material-ui/core/Typography';
import { Button, Card, CardContent, CardMedia, CircularProgress, Grid } from '@material-ui/core';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { DeltaXMLColors, MyDeltaColors } from './styling/DeltaColors';
import { SaaSEntry, SaaSTrialStatus } from './SaaSContent';
import { NavigatorData, ProductId } from './NavigatorData';
import React from 'react';
import SaaSConfirmTrial, { SubsTrialData } from './SaaSConfirmTrial';
import { ProfileDetails } from './ProfileContent';
import { useHistory } from 'react-router'
import { TrialProducts } from './Paperbase';
import QuickCompare from './QuickCompare';

interface TrialBarData {
    trialCallback: () => void;
    trialText: JSX.Element;
    trialButtonLabel: string;
    docsButtonLabel: string
}

const styles = (theme: Theme) =>
    createStyles({
        cardRoot: {
            margin: 10,
            display: 'flex',
            width: 720,
            backgroundColor: DeltaXMLColors.CONCRETE,
            height: '200px'
        },
        controls: {
            display: 'flex',
            alignItems: 'center',
            paddingLeft: theme.spacing(1),
            paddingBottom: theme.spacing(1),
            paddingTop: theme.spacing(1),
            borderTop: '1px solid ' + MyDeltaColors.FADE_OXFORD,
            height: '65px'
        },
        cardImageCover: {
            backgroundColor: DeltaXMLColors.OXFORD_BLUE,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'row',
            verticalAlign: 'middle',
        },
        cardContent: {
            width: '800px'
        },
        saasProdCardButtons: {
            margin: '2px'
        },
        cardHeader: {
            color: DeltaXMLColors.OXFORD_BLUE
        },
        buttonTrial: {
            float: 'left',
            margin: '2px'
        },
        link: {
            color: DeltaXMLColors.FOUNTAIN_BLUE
        },
        error: {
            color: DeltaXMLColors.VALENTINE_RED
        },
        progress: {
            float: 'right',
            margin: '0px 15px 0px 0px',
            color: DeltaXMLColors.OXFORD_BLUE
          }
    });

export interface SaaSProductProps extends WithStyles<typeof styles> {
    productInfo: SaaSEntry,
    profileDetails: ProfileDetails,
    saasTrialStatus: SaaSTrialStatus,
    startSubsTrial: (product: TrialProducts, trialData: SubsTrialData) => void,
    openSubscriptionDeal: (product: TrialProducts) => void
}

function SaaSProduct(props: SaaSProductProps) {
    const { classes, productInfo, profileDetails, saasTrialStatus, startSubsTrial, openSubscriptionDeal} = props;
    const [openConfirmTrial, setOpenConfirmTrial] = React.useState(false);
    const [openQuickCompareDialog, setQuickCompareDialog] = React.useState(false)

    const handleQuickCompareOpen = (open: boolean) => { 
        setQuickCompareDialog(open);
    }

    const handleConfirmTrialDialogOpen = (open: boolean) => {
        setOpenConfirmTrial(open);
    }
    const history = useHistory();

    const handleSeeDetails = () => {
        openSubscriptionDeal(productInfo.id);
        history.push(NavigatorData.subscriptionManagerBaseUrl);
    }

    let trialBarData: TrialBarData = { trialButtonLabel: '', docsButtonLabel: '', trialCallback: () => { }, trialText: <span /> };
    let showProgressCircle = false;
    //let testStatus: SaaSTrialStatus = SaaSTrialStatus.Error as SaaSTrialStatus;   
    //switch (testStatus) {
      
    switch (saasTrialStatus) {
        case SaaSTrialStatus.NotPermitted:
            trialBarData = {
                trialButtonLabel: '', docsButtonLabel: '', trialCallback: () => { },
                trialText: <span>Trial expired recently <br />Please contact <a className={classes.link} href="mailto:support@deltaxignia.com?subject=Trail not permitted. Assistance needed">support@deltaxignia.com</a></span>
            };
            break;
        case SaaSTrialStatus.Ongoing:
            trialBarData = {
                trialButtonLabel: 'Manage', docsButtonLabel: 'Get Started', trialCallback: handleSeeDetails,
                trialText: <span>Trial subscription</span>
            };
            break;
        case SaaSTrialStatus.PaidOngoing:
            trialBarData = {
                trialButtonLabel: 'Manage', docsButtonLabel: 'Get Started', trialCallback: handleSeeDetails,
                trialText: <span>Current subscription</span>
            };
            break;
        case SaaSTrialStatus.RequestingTrial:
            showProgressCircle = true;
            trialBarData = {
                trialButtonLabel: '', docsButtonLabel: '', trialCallback: () => { },
                trialText: <span>Starting trial...</span>
            };
            break;
        case SaaSTrialStatus.Unknown:
            showProgressCircle = true;
            trialBarData = {
                trialButtonLabel: '', docsButtonLabel: '', trialCallback: () => { },
                trialText: <span>Getting subscription...</span>
            };
            break;
        case SaaSTrialStatus.Permitted:
            trialBarData = {
                trialButtonLabel: 'Start Trial', docsButtonLabel: '', trialCallback: () => handleConfirmTrialDialogOpen(true),
                trialText: <span>Free trial available</span>
            };
            break;
        case SaaSTrialStatus.Error:
            trialBarData = {
                trialButtonLabel: '', docsButtonLabel: '', trialCallback: () => { },
                trialText: <><span className={classes.error}>A connection error occurred</span><span><br />Please contact <a className={classes.link} href="mailto:support@deltaxignia.com?subject=Error fetching subs state.">support@deltaxignia.com</a></span></>
            };
            break;
    }

    const showButton = trialBarData.trialButtonLabel !== '';
    const showDocsButton = trialBarData.docsButtonLabel !== '';
    const splitFooterGrid = showButton || showDocsButton || showProgressCircle;
    const gridLabelCols = splitFooterGrid ? 4 : 12;

    let docsButtonLink = '';
    switch (productInfo.id) {
        case ProductId.ccS1000D:
            docsButtonLink = 'https://docs.deltaxignia.com/content-compare-s1000d/latest/';
            break;
        case ProductId.ccJATS:
            docsButtonLink = 'https://docs.deltaxignia.com/content-compare-jats/latest/';
            break;
        case ProductId.ccBITS:
            docsButtonLink = 'https://docs.deltaxignia.com/content-compare-bits/latest/';
            break;
        case ProductId.ccNISOSTS:
            docsButtonLink = 'https://docs.deltaxignia.com/content-compare-niso-sts/latest/';
            break;
        case ProductId.ccXSLFO:
            docsButtonLink = 'https://docs.deltaxignia.com/content-compare-xsl-fo/latest/';
            break;
    }

    return (
        <Card key={productInfo.id} className={classes.cardRoot}>
            <div className={classes.cardImageCover}>
                <CardMedia
                    component="img"
                    image={productInfo.icon}
                    title={productInfo.label} />
            </div>
            <div className={classes.cardContent}>
                <CardContent>
                    <Typography gutterBottom variant="h5" className={classes.cardHeader}>
                        {productInfo.label}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {productInfo.productDesc}
                    </Typography>
                </CardContent>
                <div className={classes.controls}>
                    <Grid container spacing={0} alignItems="center">
                        <Grid item xs={gridLabelCols}>
                            <Typography variant="body2" color="textSecondary">{trialBarData.trialText}</Typography>
                        </Grid>
                        <Grid item xs={8} hidden={!splitFooterGrid}>
                            {showButton &&
                                <Button className={classes.buttonTrial} variant="contained" color="primary" onClick={trialBarData.trialCallback}>{trialBarData.trialButtonLabel}</Button> 
                            }
                            {showDocsButton &&
                                <Button className={classes.buttonTrial} variant="contained" color="primary" target="_blank" href={docsButtonLink}>{trialBarData.docsButtonLabel}</Button>
                            }
                            {showDocsButton &&
                                <>
                                    <Button className={classes.buttonTrial} variant="contained" color="primary" onClick={() => handleQuickCompareOpen(true)} >Quick Compare</Button>
                                    <QuickCompare open={openQuickCompareDialog}
                                        handleQuickCompareDialogOpen={handleQuickCompareOpen}
                                        productId={productInfo.id}
                                    />
                                </>
                            }
                            {showProgressCircle &&
                                <CircularProgress className={classes.progress} variant="indeterminate" size="30px" />
                            }
                        </Grid>
                    </Grid>
                    <SaaSConfirmTrial open={openConfirmTrial} handleConfirmTrialDialogOpen={handleConfirmTrialDialogOpen} profileDetails={profileDetails} startSubsTrial={(trialData) => {startSubsTrial(productInfo.id, trialData)}} />
                </div>
            </div>
        </Card>
    );
}

export default withStyles(styles)(SaaSProduct);